import { useState, useEffect } from "react";
import {
  Panel,
  Form,
  Button,
  Avatar,
  Tag,
  Stack,
  List,
  IconButton,
  Divider,
} from "rsuite";
import TrashIcon from "@rsuite/icons/Trash";
import { useTranslation } from "react-i18next";
import {
  changeOwnPassword,
  fetchOwnLoginAttempts,
  fetchOwnSessions,
  revokeOwnSession,
  getErrorKey,
} from "../adminApi";
import { isValidPassword } from "../util/PasswordPolicy";
import { describeUserAgent } from "../util/UserAgent";
import { getIsoDateByLanguage } from "../util/Utils";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import attemptMod from "../models/loginAttempt.json";
import sessionMod from "../models/session.json";
import MessageBox from "./../components/MessageBox";
import "./../components/AdminTables.css";
import "./Profile.css";

// Every signed-in user gets here, admin or not - it is the only place a
// non-admin can rotate their own password without asking an administrator.
const ProfilePage = () => {
  const [t, i18n] = useTranslation();
  const toast = useToast();
  const { user } = useAuth() ?? {};
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [revoking, setRevoking] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Both panels are informational; a failure here should not block the
      // password form, so the errors are swallowed rather than toasted.
      try {
        const [ownAttempts, ownSessions] = await Promise.all([
          fetchOwnLoginAttempts({ limit: 10 }),
          fetchOwnSessions(),
        ]);
        if (cancelled) return;
        setAttempts(ownAttempts);
        setSessions(ownSessions);
      } catch (error) {
        if (!cancelled) {
          setAttempts([]);
          setSessions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const weak = newPassword.length > 0 && !isValidPassword(newPassword);
  const mismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmit =
    currentPassword.length > 0 &&
    isValidPassword(newPassword) &&
    newPassword === confirmPassword &&
    newPassword !== currentPassword;

  async function submit() {
    setSaving(true);
    try {
      await changeOwnPassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success(t("profile.password-changed"), t("profile.profile"));
    } catch (error) {
      toast.error(t(getErrorKey(error)), t("profile.profile"));
    } finally {
      setSaving(false);
    }
  }

  async function revoke(sessionId) {
    setRevoking(sessionId);
    try {
      await revokeOwnSession(sessionId);
      setSessions((previous) =>
        previous.filter((element) => element[sessionMod.id] !== sessionId)
      );
      toast.success(t("active-sessions.session-revoked"), t("profile.profile"));
    } catch (error) {
      toast.error(t(getErrorKey(error)), t("profile.profile"));
    } finally {
      setRevoking(null);
    }
  }

  return (
    <>
      <Panel header={t("profile.profile")} className="content">
        <Stack spacing={16} alignItems="center" className="profile__identity">
          <Avatar size="lg" circle>
            {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
          </Avatar>
          <div>
            <div className="profile__name">{user?.name || user?.email}</div>
            <div className="profile__email">{user?.email}</div>
            {user?.role && (
              <Tag size="sm">{t("-model-user-types." + user.role)}</Tag>
            )}
          </div>
        </Stack>

        <Divider />

        <h5 className="profile__heading">{t("profile.change-password")}</h5>
        <Form fluid className="profile__form">
          <Form.Group controlId="currentPassword">
            <Form.ControlLabel>
              {t("profile.current-password")}
            </Form.ControlLabel>
            <Form.Control
              name="currentPassword"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              errorMessage={""}
              onChange={setCurrentPassword}
            />
          </Form.Group>
          <Form.Group controlId="newPassword">
            <Form.ControlLabel>{t("user-card.new-password")}</Form.ControlLabel>
            <Form.Control
              name="newPassword"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              errorMessage={""}
              onChange={setNewPassword}
              className={weak ? "status-danger" : null}
            />
            {weak && (
              <div className="caption status-danger">
                {t("auth-form.password-requirements")}
              </div>
            )}
          </Form.Group>
          <Form.Group controlId="confirmPassword">
            <Form.ControlLabel>
              {t("user-card.confirm-password")}
            </Form.ControlLabel>
            <Form.Control
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              errorMessage={""}
              onChange={setConfirmPassword}
              className={mismatch ? "status-danger" : null}
            />
            {mismatch && (
              <div className="caption status-danger">
                {t("user-card.passwords-do-not-match")}
              </div>
            )}
          </Form.Group>
          <Button
            appearance="primary"
            loading={saving}
            disabled={!canSubmit || saving}
            onClick={submit}
          >
            {t("user-card.update-password")}
          </Button>
        </Form>
      </Panel>

      <Panel header={t("profile.your-sessions")} className="content">
        {sessions.length === 0 ? (
          <MessageBox message={t("_common.empty-message")} />
        ) : (
          <List bordered>
            {sessions.map((element) => {
              const { browser, os } = describeUserAgent(element);
              const sessionId = element[sessionMod.id];
              return (
                <List.Item key={sessionId}>
                  <Stack justifyContent="space-between" alignItems="center" wrap>
                    <div>
                      <div>
                        {browser || t("login-attempts.unknown")}
                        {os ? " - " + os : ""}
                        {element[sessionMod.current] && (
                          <Tag size="sm" color="blue" className="ms-1">
                            {t("active-sessions.this-device")}
                          </Tag>
                        )}
                      </div>
                      <div className="profile__meta">
                        <span className="admin-ip">
                          {element[sessionMod.ip] || "-"}
                        </span>
                        {" - "}
                        {getIsoDateByLanguage(
                          element[sessionMod.lastSeenAt],
                          i18n.language
                        ) ?? "-"}
                      </div>
                    </div>
                    <IconButton
                      appearance="link"
                      icon={<TrashIcon />}
                      loading={revoking === sessionId}
                      disabled={!!element[sessionMod.current]}
                      title={
                        element[sessionMod.current]
                          ? t("profile.cannot-revoke-current")
                          : t("active-sessions.revoke")
                      }
                      onClick={() => revoke(sessionId)}
                    />
                  </Stack>
                </List.Item>
              );
            })}
          </List>
        )}
      </Panel>

      <Panel header={t("profile.recent-logins")} className="content">
        {attempts.length === 0 ? (
          <MessageBox message={t("_common.empty-message")} />
        ) : (
          <List bordered>
            {attempts.map((element) => {
              const { browser, os } = describeUserAgent(element);
              return (
                <List.Item key={element[attemptMod.id]}>
                  <Stack justifyContent="space-between" alignItems="center" wrap>
                    <div>
                      <Tag
                        size="sm"
                        color={element[attemptMod.success] ? "green" : "red"}
                      >
                        {element[attemptMod.success]
                          ? t("login-attempts.granted")
                          : t("login-attempts.denied")}
                      </Tag>{" "}
                      {getIsoDateByLanguage(
                        element[attemptMod.createdAt],
                        i18n.language
                      ) ?? "-"}
                    </div>
                    <div className="profile__meta">
                      <span className="admin-ip">
                        {element[attemptMod.ip] || "-"}
                      </span>
                      {" - "}
                      {browser || t("login-attempts.unknown")}
                      {os ? " - " + os : ""}
                    </div>
                  </Stack>
                </List.Item>
              );
            })}
          </List>
        )}
      </Panel>
    </>
  );
};

export default ProfilePage;
